/**
 * StrategyConfig.js
 * Central configuration for procedural generation strategies.
 * Externalizes magic numbers and weights for Melody and Bass generation.
 */

export const MELODY_WEIGHTS = {
    intervalProfiles: {
        default: { step: 0.6, skip: 0.3, leap: 0.1 },
        chant: { step: 0.9, skip: 0.1, leap: 0.0 }, // Medieval
        wide: { step: 0.3, skip: 0.4, leap: 0.3 } // Western
    },
    harmony: {
        baseDensity: 0.3, // Lower default for harmony to prevent machine-gunning
        sustainBias: 0.7  // Bias towards long notes
    },
    weights: {
        repeat: 5,
        baseMultiplier: 60,
        appoggiatura: 25,
        chordTone: 15,
        nonChordTonePenalty: 5
    },
    complexity: {
        highThreshold: 70,
        lowThreshold: 30,
        repeatPenalty: 4,
        leapBoost: 1.5,
        repeatBoost: 10
    },
    swarm: {
        count: 12,
        pitchBase: 72,
        pitchRange: 24,
        velocityBase: 0.3,
        velocityVar: 0.4
    },
    impressionist: {
        scaleSwitchProb: 0.6,
        scaleSplitProb: 0.5,
        wanderCount: 7,
        driftAmount: 0.1
    },
    minimalist: {
        phaseShiftBarInterval: 4,
        phaseShiftAmount: 0.25,
        additiveInterval: 2,
        additiveStep: 0.25
    },
    fantasy: {
        arpeggioLoop: 16,
        stepSize: 0.25
    },
    ornamentation: {
        longNoteThreshold: 1.0,
        trillProb: 0.5
    },
    ostinato: {
        cells: {
            standard: [[0, 7, 12, 7], [0, 4, 7, 12], [0, 2, 4, 7], [0, 12, 7, 12]],
            scifi: [[0, 6, 12, 6], [0, 4, 8, 12], [0, 2, 6, 8]] // Augmented/WholeTone feel
        }
    }
};

export const BASS_CONFIG = {
    octaves: {
        default: 36, // C2
        low: 24, // C1 (Sub/808)
        high: 48 // C3
    },
    velocities: {
        accent: 1.0,
        strong: 0.9,
        medium: 0.8,
        ghost: 0.4
    },
    durations: {
        quarter: 0.4,
        eighth: 0.2,
        sixteenth: 0.15,
        staccato: 0.1
    },
    funk: {
        defaultSyncopation: 50,
        cacheKey: 'funkPatterns'
    },
    cliche: {
        length: 4,
        interval: 1 // semitone
    },
    baroque: {
        notesPerBar: 8,
        stepThreshold: 2
    },
    drone: {
        duration: 4.0, // Whole bar
        velocity: 0.7
    },
    noir: {
        duration: 2.0, // Half notes
        velocity: 0.65 // Laid back
    },
    western: {
        velocity: 0.9,
        duration: 0.4
    }
};

export const BASS_WEIGHTS = {
    default: {
        anchoring: 0.5,
        chromatic: 0.1,
        syncopated: 0.2,
        motif_locked: 0.2
    },
    high_energy: {
        anchoring: 0.2,
        chromatic: 0.1,
        syncopated: 0.5,
        motif_locked: 0.3
    },
    complex: {
        anchoring: 0.2,
        chromatic: 0.3,
        syncopated: 0.2,
        motif_locked: 0.3
    }
};

export const ORCHESTRATION_WEIGHTS = {
    baseScore: 0.5,
    minQualification: 0.1,
    penalties: {
        missingRole: 0.0, // Sets score to 0 (unless softened)
        melodyIfHarmony: 0.3, // Soft penalty for Piano doing Melody
        counterIfMelody: 0.1
    },
    bonuses: {
        bass: { lowSpectral: 0.4, longSustain: 0.1 },
        melody: { highMidSpectral: 0.3, brightTimbre: 0.1, monophonic: 0.2 },
        harmony: { midFullSpectral: 0.3, polyphonic: 0.4, warmTimbre: 0.1 },
        counter: { highMidSpectral: 0.2, familyMatch: 0.2 },
        percussion: { familyMatch: 0.5, lowSpectral: 0.2 }
    }
};

export const HARMONY_WEIGHTS = {
    secondaryDominantChance: 0.2,
    pivotModulationChance: 0.1,
    borrowedChance: 0.15,
    mutationChance: 0.1
};

export const GROOVE_CONFIG = {
    tightnessBase: 0.5, // 0.5 to 1.0 (1.0 = strict grid)
    breathingAmp: 0.005, // 5ms drift
    humanizeJitter: 0.02
};

export const VOICING_LIMITS = {
    lowIntervalLimit: 48, // C3
    melodyClashThreshold: 72,
    guitarStretch: 19 // Semitones
};

export const BASE_GROOVES = {
    straight: { kick: 0, snare: 0, hihat: 0, bass: 0, melody: 0 },
    laid_back: { kick: 0, snare: 0.02, hihat: 0.01, bass: 0.005, melody: 0.03 },
    rushed: { kick: 0, snare: -0.01, hihat: -0.015, bass: -0.01, melody: 0 },
    dilla: { kick: 0.01, snare: 0.03, hihat: -0.01, bass: 0.02 },
    swing_jazz: { kick: 0, snare: 0.01, hihat: 0.02, bass: 0.01, swing: 0.66 }
};

// --- OPTIMIZATION PRESETS (Refactor Phase) ---

export const RANGE_PRESETS = {
    // Standard Roles
    standard_bass: { max: 53 },
    standard_melody: { min: 72 },

    // Strict Spectral Zones (Aligned with ScoringEngine)
    spectral_low: { min: 24, max: 53 },   // < 54 (F3)
    spectral_mid: { min: 54, max: 70 },   // 54-70 (F#3 - Bb4)
    spectral_high: { min: 71, max: 108 }, // > 70 (B4)

    // Genre Specifics (Still useful but must respect zones better)
    epic_melody: { min: 72 }, // Force High register (C5+) to avoid Mid congestion
    action_melody: { min: 60, max: 96 },
    action_pad: { min: 36, max: 60 },
    action_rhythmic: { min: 48, max: 70 }, // Cap at 70 to stay in Mid

    epic_rhythmic: { min: 48, max: 67 }, // Cap at G4 to leave room for Melody
    epic_harmony: { min: 48, max: 67 },  // Strict Mid

    horror_harmony: { min: 36, max: 96 }, // Wide range
    fantasy_harmony: { min: 36, max: 96 },

    mid_harmony: { min: 48, max: 70 }, // Tuned for Mid Bucket
    high_color: { min: 72, max: 108 },

    baroque_melody: { min: 76, max: 100 },
    baroque_harmony_color: { min: 72, max: 96 }
};

export const MELODIC_PROFILE_PRESETS = {
    default: {
        stepBonus: 50,
        leapPenalty: 1.0,
        maxInterval: 7,
        forceSteps: false
    },
    epic_lyrical: {
        stepBonus: 500, // Boosted from 300
        leapPenalty: 4.0, // Multiplier increased
        maxInterval: 2, // Strict Stepwise (Major 2nd)
        forceSteps: true
    },
    impressionist: {
        stepBonus: 2000,
        leapPenalty: 5.0,
        maxInterval: 2,
        forceSteps: true,
        allowAppoggiatura: false,
        allowNonChordTones: true
    },
    baroque: {
        stepBonus: 800, // Nuclear boost for Stepwise motion
        leapPenalty: 5.0,
        maxInterval: 2, // Scalar
        forceSteps: true,
        strictMode: true
    },
    romantic: {
        stepBonus: 100,
        leapPenalty: 1.5,
        maxInterval: 12, // Allow expressive leaps
        allowAppoggiatura: true
    }
};

export const ROLE_BEHAVIORS = {
    baroque_bass: { strategy: 'polyphonic_chord', rhythmStrategy: 'rhythmic_comp' },
    standard_bass: { strategy: 'monophonic_root' },
    horror_cloud: { strategy: 'aleatoric_cloud', rhythmStrategy: 'drone' },
    epic_ostinato: { strategy: 'polyphonic_chord', rhythmStrategy: 'rhythmic_comp' },
    epic_bass: { strategy: 'monophonic_root', rhythmStrategy: 'rhythmic_comp' } // Force rhythmic bass
};
